import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/components/auth/AuthErrorHandler";
import { LanguageToggle } from "@/components/LanguageToggle";
import { toast } from "sonner";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Clear any existing auth data on mount
    localStorage.removeItem('supabase.auth.token');
    
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log("Checking session state:", { session, error });
        
        if (error) {
          console.error("Session check error:", error);
          return;
        }
        
        if (session) {
          console.log("Valid session found, redirecting to feed");
          navigate("/feed");
        }
      } catch (error) {
        console.error("Error checking session:", error);
      }
    };
    
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session);
      
      if (event === "SIGNED_IN") {
        console.log("User signed in successfully");
        navigate("/feed");
      } else if (event === "SIGNED_OUT") {
        console.log("User signed out");
        setErrorMessage("");
        // Clear auth data on sign out
        localStorage.removeItem('supabase.auth.token');
      } else if (event === "TOKEN_REFRESHED") {
        console.log("Token refreshed successfully");
      } else if (event === "USER_UPDATED") {
        const { error } = await supabase.auth.getSession();
        if (error) {
          console.error("Auth error:", error);
          setErrorMessage(getErrorMessage(error));
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="w-full flex justify-between items-center p-4">
        <img 
          src="/app_images/Sonho_Favicon_V2.png" 
          alt="Sonho Logo" 
          className="h-12 w-12 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        />
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("auth.login")}</CardTitle>
            <CardDescription>{t("auth.loginDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {errorMessage && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                style: {
                  button: {
                    fontWeight: '600',
                    fontSize: '1rem',
                    letterSpacing: '0.025em',
                  },
                },
                variables: {
                  default: {
                    colors: {
                      brand: "#ea384c",
                      brandAccent: "#ea384c",
                    },
                  },
                },
              }}
              providers={[]}
              redirectTo={`${window.location.origin}/feed`}
              view="sign_in"
              showLinks={false}
              localization={{
                variables: {
                  sign_in: {
                    button_label: t("auth.login"),
                    email_label: t("auth.emailLabel"),
                    password_label: t("auth.passwordLabel"),
                    email_input_placeholder: t("auth.emailPlaceholder"),
                    password_input_placeholder: t("auth.passwordPlaceholder"),
                  },
                },
              }}
              onlyThirdPartyProviders={false}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;